import { findUnresolvedTemplateTokens } from '../config/contact.js';
import { legalConfig } from './legalConfig.js';
import { getShippedPublicLegalPageSlugs } from './legalSourceInventory.js';

const TRUE_VALUES = new Set(['1', 'true', 'yes', 'on']);
const TODO_PATTERN = /\bTODO\b/i;
const DRAFT_BANNER_PATTERN =
  /^\s*>\s.*\b(draft|entwurf|borrador|brouillon|launch[- ]scaffold|launch[- ]readiness|template|not legal advice|keine rechtsberatung|no es asesoramiento legal|ne constitue pas un conseil juridique)\b/im;

export const STRICT_MODE_PROTECTED_LEGAL_PAGES = Object.freeze(getShippedPublicLegalPageSlugs());

let hasValidated = false;

function readRuntimeEnv(name) {
  const viteValue = import.meta.env?.[name];
  if (typeof viteValue === 'string') {
    return viteValue;
  }

  if (typeof process !== 'undefined' && process.env) {
    return process.env[name];
  }

  return undefined;
}

function isTrueValue(value) {
  return TRUE_VALUES.has(String(value || '').trim().toLowerCase());
}

function asPageRef(entry) {
  const pageSlug = String(entry?.pageSlug || entry?.slug || 'unknown');
  const locale = String(entry?.locale || 'en');
  return `${pageSlug}[${locale}]`;
}

export function shouldEnforceRequiredLegalValues(options = {}) {
  const {
    isProduction = Boolean(import.meta.env?.PROD || readRuntimeEnv('NODE_ENV') === 'production'),
    enforceRequired = readRuntimeEnv('VITE_LEGAL_ENFORCE_REQUIRED'),
  } = options;

  return Boolean(isProduction) && isTrueValue(enforceRequired);
}

export function collectLegalConfigIssues(config = legalConfig) {
  const issues = [];
  const operator = config?.operator || {};
  const address = operator.address || {};

  if (!operator.name) {
    issues.push('VITE_LEGAL_OPERATOR_NAME fehlt.');
  }

  if (!address.street) {
    issues.push('VITE_LEGAL_OPERATOR_STREET fehlt.');
  }

  if (!address.postalCode) {
    issues.push('VITE_LEGAL_OPERATOR_POSTAL_CODE fehlt.');
  }

  if (!address.city) {
    issues.push('VITE_LEGAL_OPERATOR_CITY fehlt.');
  }

  if (!address.country) {
    issues.push('VITE_LEGAL_OPERATOR_COUNTRY fehlt.');
  }

  if (!Array.isArray(operator.addressLines) || operator.addressLines.length === 0) {
    issues.push('Ladungsfaehige Anschrift konnte nicht aus den Pflichtfeldern aufgebaut werden.');
  }

  if (!operator.email) {
    issues.push('Kontakt-E-Mail fehlt (src/config/contact.js).');
  }

  return issues;
}

export function collectLegalContentIssues(renderedPages = []) {
  const issues = [];

  for (const entry of renderedPages) {
    const pageRef = asPageRef(entry);
    const content = String(entry?.content || '');

    if (!content.trim()) {
      issues.push(`${pageRef}: gerenderter Inhalt fehlt.`);
      continue;
    }

    if (TODO_PATTERN.test(content)) {
      issues.push(`${pageRef}: enthaelt TODO-Marker.`);
    }

    if (DRAFT_BANNER_PATTERN.test(content)) {
      issues.push(`${pageRef}: enthaelt Draft-/Template-Banner.`);
    }

    const unresolvedTokens = findUnresolvedTemplateTokens(content);
    if (unresolvedTokens.length > 0) {
      issues.push(
        `${pageRef}: enthaelt unaufgeloeste Platzhalter (${unresolvedTokens.join(', ')}).`,
      );
    }
  }

  return issues;
}

function formatSourceTargetRef(target) {
  const slug = String(target?.slug || 'unknown');
  const locale = String(target?.locale || 'en');
  return `${slug}[${locale}]`;
}

export function collectLegalSourceCoverageIssues(sourceCoverage = null) {
  if (!sourceCoverage) {
    return [];
  }

  const issues = [];

  for (const target of sourceCoverage.missingSourceTargets || []) {
    issues.push(`${formatSourceTargetRef(target)}: locale-specific legal source file fehlt.`);
  }

  for (const target of sourceCoverage.duplicateSourceTargets || []) {
    const filePaths = Array.isArray(target?.filePaths) ? target.filePaths.join(', ') : '';
    issues.push(
      `${formatSourceTargetRef(target)}: mehrere Legal-Source-Dateien werden auf dasselbe Shipping-Ziel aufgeloest (${filePaths}).`,
    );
  }

  for (const entry of sourceCoverage.invalidLocalePathEntries || []) {
    issues.push(
      `${entry.filePath}: Legal-Source-Datei verwendet ein nicht kanonisches Locale-Verzeichnis und ist ueber den Site-Resolver nicht erreichbar.`,
    );
  }

  for (const entry of sourceCoverage.unsupportedLocalePathEntries || []) {
    issues.push(
      `${entry.filePath}: Legal-Source-Datei verwendet ein Locale-Verzeichnis ausserhalb der shipped Legal-Locale-Menge.`,
    );
  }

  for (const entry of sourceCoverage.unmappedSourcePathEntries || []) {
    issues.push(
      `${entry.filePath}: Legal-Source-Datei ist keinem oeffentlichen Legal-Route-Eintrag im Manifest zugeordnet.`,
    );
  }

  return issues;
}

export function getStrictModeProtectedLocales(sourceCoverage = null) {
  const locales = Array.isArray(sourceCoverage?.strictLocales)
    ? sourceCoverage.strictLocales
    : Array.isArray(sourceCoverage?.supportedLocales)
      ? sourceCoverage.supportedLocales
      : Array.isArray(sourceCoverage?.scannedLocales)
        ? sourceCoverage.scannedLocales
    : ['en'];

  return Object.freeze([...locales]);
}

export function collectLegalValidationIssues(options = {}) {
  const {
    config = legalConfig,
    renderedPages = [],
    sourceCoverage = null,
  } = options;

  const configIssues = collectLegalConfigIssues(config);
  const contentIssues = collectLegalContentIssues(renderedPages);
  const sourceCoverageIssues = collectLegalSourceCoverageIssues(sourceCoverage);
  const allContentIssues = [...contentIssues, ...sourceCoverageIssues];

  return {
    configIssues,
    contentIssues: allContentIssues,
    sourceCoverageIssues,
    issues: [...configIssues, ...allContentIssues],
  };
}

export function formatLegalValidationMessage(options = {}) {
  const {
    jurisdiction = legalConfig?.jurisdiction || 'AT',
    configIssues = [],
    contentIssues = [],
  } = options;

  return [
    `[legal] Unvollstaendige Pflichtangaben oder unsicherer Legal-Content fuer Jurisdiktion ${jurisdiction}:`,
    ...configIssues.map((issue) => `- ${issue}`),
    ...contentIssues.map((issue) => `- ${issue}`),
  ].join('\n');
}

export function validateLegalConfig(options = {}) {
  const {
    once = true,
    strict = shouldEnforceRequiredLegalValues(),
    config = legalConfig,
    renderedPages = [],
  } = options;

  if (once && hasValidated && renderedPages.length === 0) {
    return [];
  }

  const {
    configIssues,
    contentIssues,
    issues,
  } = collectLegalValidationIssues({ config, renderedPages });

  if (issues.length === 0) {
    if (once) {
      hasValidated = true;
    }
    return [];
  }

  const message = formatLegalValidationMessage({
    jurisdiction: config?.jurisdiction,
    configIssues,
    contentIssues,
  });

  if (strict) {
    throw new Error(message);
  }

  // eslint-disable-next-line no-console
  console.warn(message);
  if (once) {
    hasValidated = true;
  }
  return issues;
}
